import { useInView } from "@/hooks/useInView";
import { cn } from "@/lib/utils";

interface LiveDividerProps {
  className?: string;
  /** Show a small breathing node at center. */
  node?: boolean;
}

/**
 * Hairline gradient stroke that brightens left-to-right when scrolled into view.
 * Optional pulsing node at center.
 */
export function LiveDivider({ className, node = true }: LiveDividerProps) {
  const [ref, inView] = useInView<HTMLDivElement>();

  return (
    <div
      ref={ref}
      role="separator"
      className={cn("relative h-px w-full overflow-visible my-6", className)}
    >
      <div
        className={cn(
          "absolute inset-0 transition-[opacity,background-position] duration-1000 ease-out",
          inView ? "opacity-100" : "opacity-0"
        )}
        style={{
          background:
            "linear-gradient(90deg, transparent, hsl(var(--primary) / 0.45), hsl(var(--primary-glow) / 0.55), hsl(var(--primary) / 0.45), transparent)",
          backgroundSize: "200% 100%",
          backgroundPosition: inView ? "100% 0" : "0% 0",
        }}
      />
      {node && (
        <div
          aria-hidden
          className={cn(
            "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-1.5 w-1.5 rounded-full transition-opacity duration-700",
            inView ? "opacity-100" : "opacity-0"
          )}
          style={{
            background: "hsl(var(--primary-glow))",
            boxShadow: "0 0 12px hsl(var(--primary-glow) / 0.7)",
            animation: inView ? "breathe-soft 3.2s ease-in-out infinite" : undefined,
          }}
        />
      )}
    </div>
  );
}
